import React, { useEffect, useRef } from "react";
import Header from "../../Components/Header/Header";
import Footer from "../../Components/Footer/Footer";
import s from "./TestResults.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getTestResults } from "../../redux/InformationSlice";
import { Flex, Progress } from "antd";
import { useNavigate } from "react-router-dom";
import ScrollToTop from "../../Components/ScrollToTop/ScrollToTop";
import useWindowDimensions from "../../Components/WindowDimensions/WindowDimensions";
import MobileHeader from "../../Components/MobileHeader/MobileHeader";
import { useTranslation } from "react-i18next";

const TestResults = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { testResults } = useSelector((state: RootState) => state.info);
  const { width } = useWindowDimensions();
  const { t } = useTranslation();

  const footerRef = useRef<HTMLDivElement | null>(null);
  const scrollToFooter = () => {
    footerRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    dispatch(getTestResults());
  }, [dispatch]);

  if (!testResults) {
    return (
      <div>
        <div className={s.content}>
          {width > 1100 ? (
            <Header onContactsClick={scrollToFooter} />
          ) : (
            <MobileHeader onContactsClick={scrollToFooter} />
          )}
          <div className={s.loading}>{t("Загрузка результатов...")}</div>
        </div>
        <Footer />
      </div>
    );
  }

  // Map section codes to display info
  const getSectionInfo = (code: string, recommendation?: string) => {
    const sectionMap: Record<string, { title: string; icon: string }> = {
      H: {
        title: t("ВИЧ"),
        icon: "ribbon", // You can replace with actual icon path
      },
      S: {
        title: t("ИППП"),
        icon: "stis", // You can replace with actual icon path
      },
      T: {
        title: t("Туберкулез"),
        icon: "tuberculosis", // You can replace with actual icon path
      },
    };
    const sectionInfo = sectionMap[code] || { title: code, icon: "" };
    return {
      ...sectionInfo,
      recommendation:
        recommendation ||
        t("Рекомендация: Обратитесь к врачу для консультации"),
    };
  };

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      low: t("низкий"),
      medium: t("средний"),
      high: t("высокий"),
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      low: "#34c759", // green
      medium: "#ffcc00", // yellow
      high: "#ff3b30", // red
    };
    return colorMap[status] || "#34c759";
  };

  // Filter sections that should show in summary (H, S, T)
  const summarySections = testResults.sections.filter((section) =>
    ["H", "S", "T"].includes(section.section_code),
  );

  return (
    <div>
      <div className={s.content}>
        {width > 1100 ? (
          <Header onContactsClick={scrollToFooter} />
        ) : (
          <MobileHeader onContactsClick={scrollToFooter} />
        )}
        <div className={s.title}>{t("РЕЗЮМЕ ПО ТЕМАМ")}</div>

        {/* Summary Cards */}
        <div className={s.summary_cards}>
          {summarySections.map((section) => {
            const sectionInfo = getSectionInfo(
              section.section_code,
              section.recommendation,
            );
            const statusColor = getStatusColor(section.status);
            return (
              <div
                key={section.section_code}
                className={s.summary_card}
                style={{ borderColor: statusColor }}
              >
                <div className={s.card_icon}>
                  {/* You can replace this with actual icon images */}
                  <div className={s.icon_placeholder}>
                    {section.section_code}
                  </div>
                </div>
                <div className={s.card_title}>{sectionInfo.title}</div>
                <div className={s.card_status} style={{ color: statusColor }}>
                  {getStatusText(section.status)}
                </div>
                <div className={s.card_recommendation}>
                  {section.recommendation || sectionInfo.recommendation}
                </div>
                {section.result_image && (
                  <div className={s.card_image}>
                    <img src={section.result_image} alt={sectionInfo.title} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Detailed Results Table */}
        <div className={s.results_section}>
          <div className={s.results_title}>
            {t("Мои результаты прохождения теста")}
          </div>
          <div className={s.results_table}>
            <div className={s.results_header}>
              <div>{t("Блоки")}</div>
              <div>{t("Ответы")}</div>
              <div>{t("Прогресс")}</div>
            </div>
            {testResults.sections.map((section) => (
              <div key={section.section_code} className={s.results_row}>
                <div className={s.results_block}>
                  <div className={s.block_code}>{section.section_code}</div>
                </div>
                <div className={s.results_answers}>
                  {section.answered_count}/{section.total_count}
                </div>
                <div className={s.results_percentage}>
                  {section.progress_percentage ||
                    Math.round(
                      (section.answered_count / section.total_count) * 100,
                    )}
                  %
                </div>
                <div className={s.results_progress_bar}>
                  <Flex vertical gap="small" style={{ width: "100%" }}>
                    <Progress
                      percent={
                        section.progress_percentage ||
                        Math.round(
                          (section.answered_count / section.total_count) * 100,
                        )
                      }
                      size="small"
                      showInfo={false}
                      strokeColor={getStatusColor(section.status)}
                      strokeWidth={4}
                    />
                  </Flex>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className={s.action_buttons}>
          <button className={s.action_btn} onClick={() => navigate("/test")}>
            {t("Пройти тест снова")}
          </button>
          <button className={s.action_btn} onClick={() => navigate("/")}>
            {t("На главную")}
          </button>
        </div>
      </div>
      <div ref={footerRef}>
        <Footer />
      </div>
      <ScrollToTop />
    </div>
  );
};

export default TestResults;
